// besturimz-website/src/components/Navbar.js
import React, { useState, useEffect } from 'react'
import styled, { css } from 'styled-components'
import { Link } from 'react-router-dom'
import { FaFacebook, FaInstagram, FaTwitter, FaWhatsapp, FaPhone, FaEnvelope } from 'react-icons/fa'
import { PHONE_NUMBERS, CONTACT_EMAIL } from '../constants/contact'

const glassSurface = css`
  background: rgba(255, 255, 255, 0.68);
  backdrop-filter: blur(18px) saturate(165%);
  -webkit-backdrop-filter: blur(18px) saturate(165%);
  border-bottom: 1px solid rgba(255, 255, 255, 0.55);
  box-shadow: 0 4px 24px rgba(15, 23, 42, 0.08);
`

const NavbarContainer = styled.nav`
  ${glassSurface}
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1000;
  padding: 0;
`

const NavContent = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 70px;

  @media (max-width: 768px) {
    padding: 0 15px;
    height: 60px;
  }
`

const LogoSection = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  min-width: 0;
`

const LogoLink = styled(Link)`
  display: flex;
  align-items: center;
  gap: 12px;
  height: 100%;
  padding: 4px 0;
  text-decoration: none;
  line-height: 1;
  border-radius: 6px;
  transition: opacity 0.2s ease, transform 0.2s ease;

  &:hover {
    opacity: 0.92;
    transform: scale(1.01);
  }

  &:focus-visible {
    outline: 2px solid #c62828;
    outline-offset: 3px;
  }

  @media (max-width: 768px) {
    gap: 8px;
  }
`

const LOGO_SRC = `${process.env.PUBLIC_URL}/besturizm-logo-transparent.svg`

// SVG viewBox: 595.5 × 842.25 — gerçek logo alanı
const LOGO_CONTENT = { x: 82.21, y: 264.57, w: 430.58, h: 312.32 }
const LOGO_VIEWBOX = { w: 595.5, h: 842.25 }

const getLogoMetrics = (iconHeight) => {
  const scale = iconHeight / LOGO_CONTENT.h
  return {
    wrapWidth: LOGO_CONTENT.w * scale,
    imageHeight: LOGO_VIEWBOX.h * scale,
    offsetLeft: -LOGO_CONTENT.x * scale,
    offsetTop: -LOGO_CONTENT.y * scale,
  }
}

const desktopLogo = getLogoMetrics(58)
const mobileLogo = getLogoMetrics(46)

const LogoIconWrap = styled.div`
  position: relative;
  flex-shrink: 0;
  overflow: hidden;
  height: 58px;
  width: ${desktopLogo.wrapWidth}px;
  background: transparent;

  @media (max-width: 768px) {
    height: 46px;
    width: ${mobileLogo.wrapWidth}px;
  }
`

const LogoImage = styled.img`
  position: absolute;
  display: block;
  height: ${desktopLogo.imageHeight}px;
  width: auto;
  max-width: none;
  left: ${desktopLogo.offsetLeft}px;
  top: ${desktopLogo.offsetTop}px;
  background: transparent;
  pointer-events: none;

  @media (max-width: 768px) {
    height: ${mobileLogo.imageHeight}px;
    left: ${mobileLogo.offsetLeft}px;
    top: ${mobileLogo.offsetTop}px;
  }
`

const BrandName = styled.span`
  display: inline-flex;
  align-items: baseline;
  font-size: 1.55rem;
  font-weight: 800;
  letter-spacing: 0.04em;
  line-height: 1;
  white-space: nowrap;

  @media (max-width: 768px) {
    font-size: 1.2rem;
    letter-spacing: 0.03em;
  }
`

const BrandNameRed = styled.span`
  background: linear-gradient(180deg, #ef5350 0%, #c62828 45%, #b71c1c 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: #c62828;
`

const BrandNameBlue = styled.span`
  background: linear-gradient(180deg, #42a5f5 0%, #1565c0 50%, #0d47a1 100%);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  color: #1565c0;
`

const NavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 885px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    ${glassSurface}
    flex-direction: column;
    padding: 1rem 0;
    gap: 0;
    max-height: 60vh;
    overflow-y: auto;
  }

  @media (max-width: 768px) {
    display: ${props => props.isOpen ? 'flex' : 'none'};
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    ${glassSurface}
    flex-direction: column;
    padding: 1rem 0;
    gap: 0;
    max-height: 60vh;
    overflow-y: auto;
  }
`

const MobileContactSection = styled.div`
  display: none;
  
  @media (max-width: 885px) {
    display: flex;
    justify-content: center;
    gap: 1rem;
    padding: 1rem;
    border-top: 1px solid rgba(255, 255, 255, 0.35);
    margin-top: 1rem;
  }
`

const NavLink = styled(Link)`
  text-decoration: none;
  color: #333;
  font-weight: 500;
  font-size: 0.9rem;
  padding: 0.5rem 0;
  transition: color 0.3s ease;
  position: relative;

  &:hover {
    color: #667eea;
  }

  &::after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 0;
    height: 2px;
    background: #667eea;
    transition: width 0.3s ease;
  }

  &:hover::after {
    width: 100%;
  }

  @media (max-width: 768px) {
    padding: 0.8rem 1rem;
    width: 100%;
    text-align: center;
    border-bottom: 1px solid rgba(255, 255, 255, 0.35);

    &::after {
      display: none;
    }
  }
`

const ContactSection = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;

  @media (max-width: 768px) {
    gap: 0.5rem;
  }
`

const SocialIcons = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;

  @media (max-width: 885px) {
    display: none;
  }
`

const SocialIcon = styled.a`
  color: #666;
  font-size: 1.2rem;
  transition: color 0.3s ease;

  &:hover {
    color: #667eea;
  }
`

const ContactInfo = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;

  @media (max-width: 885px) {
    display: none;
  }
`

const MobileContactInfo = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.5rem;
`

const ContactIcon = styled.div`
  color: #667eea;
  font-size: 1rem;
  cursor: pointer;
  transition: color 0.3s ease;

  &:hover {
    color: #5a67d8;
  }
`

const Tooltip = styled.div`
  position: absolute;
  top: 100%;
  right: 0;
  background: #333;
  color: white;
  padding: 0.65rem 0.85rem;
  border-radius: 6px;
  font-size: 0.8rem;
  white-space: nowrap;
  z-index: 1001;
  opacity: ${props => props.show ? 1 : 0};
  visibility: ${props => props.show ? 'visible' : 'hidden'};
  transition: all 0.3s ease;
  margin-top: 0.5rem;
  display: flex;
  flex-direction: column;
  gap: 0.35rem;

  a {
    color: white;
    text-decoration: none;

    &:hover {
      text-decoration: underline;
    }
  }

  &::before {
    content: '';
    position: absolute;
    bottom: 100%;
    right: 15px;
    border: 5px solid transparent;
    border-bottom-color: #333;
  }
`

const MobileMenuButton = styled.button`
  display: none;
  background: none;
  border: none;
  font-size: 1.5rem;
  color: #333;
  cursor: pointer;

  @media (max-width: 885px) {
    display: block;
  }
`

const DesktopNavLinks = styled.div`
  display: flex;
  gap: 2rem;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`

const Navbar = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [showPhoneTooltip, setShowPhoneTooltip] = useState(false)
  const [showEmailTooltip, setShowEmailTooltip] = useState(false)
  const [screenWidth, setScreenWidth] = useState(window.innerWidth)

  // Ekran boyutu değişikliklerini dinle
  useEffect(() => {
    const handleResize = () => {
      setScreenWidth(window.innerWidth)
      
      // Ekran genişliği 885px'ten büyükse menüyü kapat
      if (window.innerWidth > 885 && isMenuOpen) {
        setIsMenuOpen(false)
      }
    }

    window.addEventListener('resize', handleResize)
    
    return () => {
      window.removeEventListener('resize', handleResize)
    }
  }, [isMenuOpen])

  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen)
  }

  const togglePhoneTooltip = () => {
    setShowPhoneTooltip(!showPhoneTooltip)
    setShowEmailTooltip(false) // Diğer tooltip'i kapat
  }

  const toggleEmailTooltip = () => {
    setShowEmailTooltip(!showEmailTooltip)
    setShowPhoneTooltip(false) // Diğer tooltip'i kapat
  }

  const closeAllTooltips = () => {
    setShowPhoneTooltip(false)
    setShowEmailTooltip(false)
  }

  const handleNavClick = () => {
    setIsMenuOpen(false)
    closeAllTooltips()
  }

  return (
    <NavbarContainer onClick={closeAllTooltips}>
      <NavContent>
        {/* Logo */}
        <LogoSection>
          <LogoLink to="/" aria-label="Besturizm Ana Sayfa" onClick={handleNavClick}>
            <LogoIconWrap>
              <LogoImage src={LOGO_SRC} alt="Besturizm" />
            </LogoIconWrap>
            <BrandName>
              <BrandNameRed>BES</BrandNameRed>
              <BrandNameBlue>TURİZM</BrandNameBlue>
            </BrandName>
          </LogoLink>
        </LogoSection>

        {/* Navigasyon Linkleri */}
        <NavLinks isOpen={isMenuOpen}>
          {/* Sayfa Linkleri - Sadece 768px altında görünür */}
          <div style={{ 
            display: screenWidth <= 768 ? 'flex' : 'none',
            flexDirection: 'column',
            width: '100%'
          }}>
            <NavLink to="/" onClick={handleNavClick}>Ana Sayfa</NavLink>
            <NavLink to="/#flights" onClick={handleNavClick}>Bilet Hizmetlerimiz</NavLink>
            <NavLink to="/#accommodation" onClick={handleNavClick}>Otel & Konaklama</NavLink>
            <NavLink to="/#about" onClick={handleNavClick}>Hakkımızda & İletişim</NavLink>
          </div>
          
          {/* Mobil İletişim Bölümü */}
          <MobileContactSection>
            <SocialIcon href="#" target="_blank" rel="noopener noreferrer">
              <FaFacebook />
            </SocialIcon>
            <SocialIcon href="#" target="_blank" rel="noopener noreferrer">
              <FaInstagram />
            </SocialIcon>
            <SocialIcon href="#" target="_blank" rel="noopener noreferrer">
              <FaTwitter />
            </SocialIcon>
            <SocialIcon href="#" target="_blank" rel="noopener noreferrer">
              <FaWhatsapp />
            </SocialIcon>
            <MobileContactInfo onClick={(e) => e.stopPropagation()}>
              <ContactIcon onClick={togglePhoneTooltip}>
                <FaPhone />
              </ContactIcon>
              <Tooltip show={showPhoneTooltip}>
                {PHONE_NUMBERS.map((phone) => (
                  <a key={phone.tel} href={`tel:${phone.tel}`}>
                    {phone.display}
                  </a>
                ))}
              </Tooltip>
            </MobileContactInfo>
            <MobileContactInfo onClick={(e) => e.stopPropagation()}>
              <ContactIcon onClick={toggleEmailTooltip}>
                <FaEnvelope />
              </ContactIcon>
              <Tooltip show={showEmailTooltip}>
                {CONTACT_EMAIL}
              </Tooltip>
            </MobileContactInfo>
          </MobileContactSection>
        </NavLinks>

        {/* Desktop Navigasyon Linkleri */}
        <DesktopNavLinks>
          <NavLink to="/" onClick={handleNavClick}>Ana Sayfa</NavLink>
          <NavLink to="/#flights" onClick={handleNavClick}>Bilet Hizmetlerimiz</NavLink>
          <NavLink to="/#accommodation" onClick={handleNavClick}>Otel & Konaklama</NavLink>
          <NavLink to="/#about" onClick={handleNavClick}>Hakkımızda & İletişim</NavLink>
        </DesktopNavLinks>

        {/* İletişim ve Sosyal Medya */}
        <ContactSection>
          <SocialIcons>
            <SocialIcon href="#" target="_blank" rel="noopener noreferrer">
              <FaFacebook />
            </SocialIcon>
            <SocialIcon href="#" target="_blank" rel="noopener noreferrer">
              <FaInstagram />
            </SocialIcon>
            <SocialIcon href="#" target="_blank" rel="noopener noreferrer">
              <FaTwitter />
            </SocialIcon>
            <SocialIcon href="#" target="_blank" rel="noopener noreferrer">
              <FaWhatsapp />
            </SocialIcon>
          </SocialIcons>

          <ContactInfo onClick={(e) => e.stopPropagation()}>
            <ContactIcon onClick={togglePhoneTooltip}>
              <FaPhone />
            </ContactIcon>
            <Tooltip show={showPhoneTooltip}>
              {PHONE_NUMBERS.map((phone) => (
                <a key={phone.tel} href={`tel:${phone.tel}`}>
                  {phone.display}
                </a>
              ))}
            </Tooltip>
          </ContactInfo>

          <ContactInfo onClick={(e) => e.stopPropagation()}>
            <ContactIcon onClick={toggleEmailTooltip}>
              <FaEnvelope />
            </ContactIcon>
            <Tooltip show={showEmailTooltip}>
              {CONTACT_EMAIL}
            </Tooltip>
          </ContactInfo>

          <MobileMenuButton onClick={toggleMenu}>
            {isMenuOpen ? '✕' : '☰'}
          </MobileMenuButton>
        </ContactSection>
      </NavContent>
    </NavbarContainer>
  )
}

export default Navbar